'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { curatorQueries } from '../../query-factory';
import { useDebounceCallback } from './useDebounceCallback';

interface UseSnsDuplicateCheckOptions {
  url?: string;
  enabled?: boolean;
  debounceDelay?: number;
  onStatusChange?: (status: { isChecking: boolean; isDuplicated: boolean | null } | null) => void;
}

interface UseSnsDuplicateCheckResult {
  isChecking: boolean;
  isDuplicated: boolean | null;
  error: Error | null;
}

export const useSnsDuplicateCheck = ({
  url,
  enabled = true,
  debounceDelay = 500,
  onStatusChange,
}: UseSnsDuplicateCheckOptions = {}): UseSnsDuplicateCheckResult => {
  const [urlToCheck, setUrlToCheck] = useState('');
  const lastStatusKeyRef = useRef<string | null>(null);

  const debouncedUpdateUrl = useDebounceCallback((nextUrl: string) => {
    setUrlToCheck(nextUrl.trim());
  }, debounceDelay);

  const notifyStatus = useCallback(
    (status: { isChecking: boolean; isDuplicated: boolean | null } | null) => {
      onStatusChange?.(status);
    },
    [onStatusChange],
  );

  useEffect(() => {
    if (!enabled) {
      setUrlToCheck('');
      lastStatusKeyRef.current = null;
      notifyStatus(null);
      return;
    }

    const trimmedUrl = (url ?? '').trim();

    if (!trimmedUrl) {
      setUrlToCheck('');
      lastStatusKeyRef.current = null;
      notifyStatus(null);
      return;
    }

    debouncedUpdateUrl(trimmedUrl);
  }, [url, enabled, debouncedUpdateUrl, notifyStatus]);

  const shouldFetch = useMemo(() => enabled && urlToCheck.length > 0, [enabled, urlToCheck]);

  const { data, error, isFetching } = useQuery({
    ...curatorQueries.join.checkSnsDuplicate({ url: urlToCheck }),
    enabled: shouldFetch,
    retry: false,
  });

  const rawDuplicated = data?.isDuplicated ?? null;
  const isDuplicated = isFetching ? null : rawDuplicated;
  const normalizedError = error instanceof Error ? error : null;

  useEffect(() => {
    const status = shouldFetch ? { isChecking: isFetching, isDuplicated } : null;
    const nextKey = status ? `${status.isChecking}-${status.isDuplicated}` : null;

    if (lastStatusKeyRef.current === nextKey) {
      return;
    }

    lastStatusKeyRef.current = nextKey;
    notifyStatus(status);
  }, [shouldFetch, isFetching, isDuplicated, notifyStatus]);

  return {
    isChecking: isFetching,
    isDuplicated,
    error: normalizedError,
  };
};
